import { parseExpression, SEGMENT_TYPES, PATH_SOURCES } from './expressionParser';
import { isSensitiveTokenPath } from './automationDataCatalog';

// Presentation policy only. Never migrate, filter or rewrite stored workflows.
// New/unknown capabilities are advanced until a simple editor explicitly supports them.
export const SIMPLE_ACTION_TYPES = ['updateField', 'sendEmail', 'sendNotification', 'addonAction'];
export const SIMPLE_TRIGGER_TYPES = ['update', 'create', 'manual', 'scheduled'];
// Field changes are selected in the trigger's monitoredFields. Legacy
// fieldChanged conditions only test existence in the runtime, so keep them advanced.
export const SIMPLE_CONDITION_TYPES = ['fieldValue'];
export const SIMPLE_CONDITION_OPERATORS = ['==', '!=', '>', '>=', '<', '<=', 'contains', 'startsWith', 'endsWith'];

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const onlyKeys = (value, keys) => isObject(value) && Object.keys(value).every((key) => keys.includes(key));
const isEmpty = (value) => value === undefined || value === null || value === '';
const fieldPath = (value) => isEmpty(value) || (typeof value === 'string' &&
  /^[\w]+(?:\.[\w]+)*$/.test(value) && !isSensitiveTokenPath(value));
const SIMPLE_REFERENCE_PATHS = [
  'name', 'documentIdentifiant', 'displayCode', 'dependencyDisplayCode',
  'assignedToId', 'assignedToName', 'dependencyName', 'targetName', 'targetProfileName', 'ownerName',
];

/** Scalars and source-field/date tokens that the no-code value editor can represent. */
export function isSimpleValue(value) {
  if (isEmpty(value) || typeof value === 'boolean' || typeof value === 'number') return true;
  if (typeof value !== 'string') return false;
  const parsed = parseExpression(value);
  if (parsed.warnings.length) return false;
  return parsed.segments.every((segment) => {
    if (segment.type === SEGMENT_TYPES.TEXT) return true;
    // The display parser also identifies @domain in a literal email address.
    if (segment.type === SEGMENT_TYPES.AT_FIELD && segment.precededByWordChar) return true;
    if ([SEGMENT_TYPES.DOLLAR, SEGMENT_TYPES.PERCENT].includes(segment.type)) return false;
    if ([PATH_SOURCES.SYSTEM, PATH_SOURCES.ASYNC].includes(segment.source)) return false;
    if ([PATH_SOURCES.DATE, PATH_SOURCES.INVOICE_DATE].includes(segment.source)) {
      return ['todayDate', 'invoiceDate'].includes(segment.path);
    }
    return fieldPath(segment.path) && (
      segment.path?.startsWith('data.') || segment.path?.startsWith('finances.') ||
      SIMPLE_REFERENCE_PATHS.includes(segment.path)
    );
  });
}

export const isSimpleActionType = (type) => SIMPLE_ACTION_TYPES.includes(type);

export function isSimpleStep(step) {
  if (!onlyKeys(step, ['id', 'name', 'type', 'config', 'conditions', 'outputVariables', 'stopOnSuccess', 'stopOnError']) ||
      !isSimpleActionType(step.type)) return false;
  if (step.stopOnSuccess || step.stopOnError === false) return false;
  if (step.conditions != null && (!Array.isArray(step.conditions) || step.conditions.length)) return false;
  if (step.outputVariables != null && (!isObject(step.outputVariables) || Object.keys(step.outputVariables).length)) return false;
  const config = step.config === undefined ? {} : step.config;
  if (step.type === 'addonAction') {
    return onlyKeys(config, ['capability', 'actionKey', 'params']) && config.capability === 'booking.invite' && config.actionKey === 'booking.invite'
      && onlyKeys(config.params || {}, ['scenarioId', 'templateId'])
      && Object.values(config.params || {}).every(value => isEmpty(value) || typeof value === 'string' && /^[\w-]+$/.test(value));
  }
  if (step.type === 'updateField') {
    if (!onlyKeys(config, ['field', 'value', 'groupsMode', 'targetElement']) ||
        !isEmpty(config.targetElement) || !fieldPath(config.field)) return false;
    const isGroups = ['groups', 'data.groups'].includes(config.field);
    if (!isEmpty(config.groupsMode) && (!isGroups || !['replace', 'merge', 'remove'].includes(config.groupsMode))) return false;
    return isGroups && Array.isArray(config.value)
      ? config.value.every((item) => typeof item === 'string' && isSimpleValue(item))
      : isSimpleValue(config.value);
  }
  if (step.type === 'sendEmail') {
    if (!onlyKeys(config, ['templateId', 'pdfTemplateId', 'recipientEmailField', 'manualEmail'])) return false;
    const recipientField = config.recipientEmailField;
    return [config.templateId, config.pdfTemplateId].every((value) => isEmpty(value) ||
      (typeof value === 'string' && !/[{}@$%]/.test(value))) &&
      (isEmpty(recipientField) || (typeof recipientField === 'string' && recipientField.startsWith('@') && fieldPath(recipientField.slice(1)))) &&
      isSimpleValue(config.manualEmail);
  }
  return onlyKeys(config, ['recipient', 'subject']) &&
    isSimpleValue(config.recipient) && isSimpleValue(config.subject);
}

export function isSimpleCondition(condition) {
  if (!onlyKeys(condition, ['id', 'type', 'logic', 'operator', 'config']) ||
      !SIMPLE_CONDITION_TYPES.includes(condition.type) ||
      ![undefined, null, '', 'AND'].includes(condition.logic) ||
      ![undefined, null, '', 'AND'].includes(condition.operator)) return false;
  const config = condition.config === undefined ? {} : condition.config;
  // Trigger conditions compare literal values; they do not resolve the action
  // editor's dynamic tokens. Never suggest that an expression will be evaluated.
  const literalValue = typeof config?.value !== 'string' || parseExpression(config.value).segments.every((segment) =>
    segment.type === SEGMENT_TYPES.TEXT || (segment.type === SEGMENT_TYPES.AT_FIELD && segment.precededByWordChar));
  return onlyKeys(config, ['field', 'operator', 'value']) && fieldPath(config.field) &&
    (isEmpty(config.operator) || SIMPLE_CONDITION_OPERATORS.includes(config.operator)) &&
    isSimpleValue(config.value) && literalValue;
}

const TRIGGER_KEYS = ['type', 'approvalEvent', 'monitoredFields', 'scheduleType', 'dayOfMonth', 'dayOfWeek', 'timeOfDay', 'cronExpression', 'radius', 'triggerOn'];
const RECORD_METADATA_KEYS = ['id', 'name', 'description', 'structureId', 'ownerId', 'isActive',
  'createdAt', 'lastUpdated', 'createdBy', 'updatedBy', 'timestamp', 'timeStamp', 'lastUpdate',
  'executionCount', 'errorCount', 'lastExecutedAt', 'isDeleted', 'deletedAt', 'deletedBy'];
const STORED_TRIGGER_KEYS = [...RECORD_METADATA_KEYS, ...TRIGGER_KEYS.filter((key) => key !== 'type'),
  'triggerType', 'workflowId', 'workflowName', 'conditions'];
const STORED_WORKFLOW_KEYS = [...RECORD_METADATA_KEYS, 'triggerType', 'steps', 'maxExecutionPerDoc'];

function isSimpleView(automation) {
  const trigger = automation?.trigger;
  if (!onlyKeys(trigger, TRIGGER_KEYS) || !SIMPLE_TRIGGER_TYPES.includes(trigger.type)) return false;
  if (!isEmpty(trigger.cronExpression) || trigger.scheduleType === 'custom') return false;
  if (trigger.type === 'scheduled' && !['daily', 'weekly', 'monthly'].includes(trigger.scheduleType)) return false;
  if (trigger.monitoredFields != null && (!Array.isArray(trigger.monitoredFields) || !trigger.monitoredFields.every(fieldPath))) return false;
  if (!isEmpty(automation.maxExecutionPerDoc)) return false;
  return Array.isArray(automation.conditions) && automation.conditions.every(isSimpleCondition) &&
    Array.isArray(automation.steps) && automation.steps.every(isSimpleStep);
}

/** Also inspect original snapshots: UI defaults must not hide an advanced legacy shape. */
export function isSimpleAutomation(automation) {
  if (!isSimpleView(automation)) return false;
  const source = automation?._source;
  if (!source) return true;
  if (!isSimpleView(source.view)) return false;
  if (source.trigger && (!onlyKeys(source.trigger, STORED_TRIGGER_KEYS) ||
      !SIMPLE_TRIGGER_TYPES.includes(source.trigger.triggerType) ||
      (source.trigger.conditions != null && (!Array.isArray(source.trigger.conditions) || !source.trigger.conditions.every(isSimpleCondition))))) return false;
  if (source.workflow && (!onlyKeys(source.workflow, STORED_WORKFLOW_KEYS) ||
      !Array.isArray(source.workflow.steps) || !source.workflow.steps.every(isSimpleStep))) return false;
  return true;
}
